import { z } from 'zod';
import { paginationMetaSchema } from '@/api/pagination';

export const systemDashboardRowSchema = z.object({
  userId: z.coerce.number(),
  name: z.string(),
  hrEmpId: z.string().nullable(),
  mobile: z.string().nullable(),
  amUserId: z.coerce.number().nullable(),
  amName: z.string().nullable(),
  amMobile: z.string().nullable(),
  departmentName: z.string().nullable(),
  divisionName: z.string().nullable(),
  districtName: z.string().nullable(),
  thanaName: z.string().nullable(),
  dcrEntered: z.number(),
  dcrApproved: z.number(),
  derEntered: z.number(),
  derApproved: z.number(),
  dcrClaimedAmount: z.number(),
  dcrApprovedAmount: z.number(),
  derClaimedAmount: z.number(),
  derApprovedAmount: z.number(),
  receivedQty: z.number(),
  returnedQty: z.number(),
  deliveredQty: z.number(),
  instituteCount: z.number(),
  teacherCount: z.number(),
});

const systemDashboardTotalsSchema = systemDashboardRowSchema.pick({
  dcrEntered: true,
  dcrApproved: true,
  derEntered: true,
  derApproved: true,
  dcrClaimedAmount: true,
  dcrApprovedAmount: true,
  derClaimedAmount: true,
  derApprovedAmount: true,
  receivedQty: true,
  returnedQty: true,
  deliveredQty: true,
  instituteCount: true,
  teacherCount: true,
});

export const systemDashboardResponseSchema = z.object({
  data: z.array(systemDashboardRowSchema),
  meta: paginationMetaSchema.extend({ totals: systemDashboardTotalsSchema }),
});

export type SystemDashboardRow = z.infer<typeof systemDashboardRowSchema>;
export type SystemDashboardResponse = z.infer<typeof systemDashboardResponseSchema>;

export const dcrDashboardRowSchema = z.object({
  userId: z.coerce.number(),
  name: z.string(),
  hrEmpId: z.string().nullable(),
  mobile: z.string().nullable(),
  departmentName: z.string().nullable(),
  designationName: z.string().nullable(),
  divisionName: z.string().nullable(),
  districtName: z.string().nullable(),
  thanaName: z.string().nullable(),
  totalDcr: z.number(),
  sdCount: z.number(),
  mpCount: z.number(),
  tcCount: z.number(),
  lcCount: z.number(),
  owCount: z.number(),
  csrCount: z.number(),
  lastUpdate: z.coerce.date().nullable(),
});

const dcrDashboardTotalsSchema = dcrDashboardRowSchema.pick({
  totalDcr: true,
  sdCount: true,
  mpCount: true,
  tcCount: true,
  lcCount: true,
  owCount: true,
  csrCount: true,
});

export const dcrDashboardResponseSchema = z.object({
  data: z.array(dcrDashboardRowSchema),
  meta: paginationMetaSchema.extend({ totals: dcrDashboardTotalsSchema }),
});

export type DcrDashboardRow = z.infer<typeof dcrDashboardRowSchema>;
export type DcrDashboardResponse = z.infer<typeof dcrDashboardResponseSchema>;
export type DcrDashboardTotals = z.infer<typeof dcrDashboardTotalsSchema>;

export const derDashboardColumnSchema = z.object({
  id: z.coerce.number(),
  name: z.string(),
  code: z.string().nullable(),
});

export const derDashboardRowSchema = z.object({
  userId: z.coerce.number(),
  name: z.string(),
  hrEmpId: z.string().nullable(),
  mobile: z.string().nullable(),
  amUserId: z.coerce.number().nullable(),
  amName: z.string().nullable(),
  divisionName: z.string().nullable(),
  districtName: z.string().nullable(),
  thanaName: z.string().nullable(),
  amounts: z.record(z.string(), z.number()),
  total: z.number(),
});

export const derDashboardResponseSchema = z.object({
  data: z.array(derDashboardRowSchema),
  meta: paginationMetaSchema.extend({
    columns: z.array(derDashboardColumnSchema),
    totals: z.object({
      total: z.number(),
      byExpenseType: z.record(z.string(), z.number()),
    }),
  }),
});

export type DerDashboardColumn = z.infer<typeof derDashboardColumnSchema>;
export type DerDashboardRow = z.infer<typeof derDashboardRowSchema>;
export type DerDashboardResponse = z.infer<typeof derDashboardResponseSchema>;

const approvalStatusSchema = z.enum(['approved', 'rejected', 'pending']);

export const workSummarySchema = z.object({
  profile: z.object({
    userId: z.coerce.number(),
    name: z.string(),
    hrEmpId: z.string().nullable(),
    mobile: z.string().nullable(),
    amName: z.string().nullable(),
    departmentName: z.string().nullable(),
    designationName: z.string().nullable(),
    divisionName: z.string().nullable(),
    districtName: z.string().nullable(),
    thanaName: z.string().nullable(),
  }),
  period: z.object({ dateFrom: z.string(), dateTo: z.string() }),
  dcr: z.array(
    z.object({
      recordId: z.coerce.number(),
      dcrDate: z.coerce.date(),
      workType: z.string().nullable(),
      instituteName: z.string().nullable(),
      thanaName: z.string().nullable(),
      districtName: z.string().nullable(),
      divisionName: z.string().nullable(),
      meetPersonMobile: z.string().nullable(),
      areaFrom: z.string().nullable(),
      areaTo: z.string().nullable(),
      comments: z.string().nullable(),
      timeFrom: z.string().nullable(),
      timeTo: z.string().nullable(),
      transportType: z.string().nullable(),
      fareAmount: z.number(),
      approveFareAmount: z.number(),
      status: approvalStatusSchema,
    }),
  ),
  expenses: z.array(
    z.object({
      detailId: z.coerce.number(),
      expenseDate: z.coerce.date(),
      expenseType: z.string().nullable(),
      amount: z.number(),
      approveAmount: z.number(),
      vendor: z.string().nullable(),
      comments: z.string().nullable(),
      status: approvalStatusSchema,
    }),
  ),
  totals: z.object({
    dcrCount: z.number(),
    fareAmount: z.number(),
    approveFareAmount: z.number(),
    expenseCount: z.number(),
    expenseAmount: z.number(),
    approveExpenseAmount: z.number(),
  }),
});

export type WorkSummary = z.infer<typeof workSummarySchema>;
export type WorkSummaryDcrRow = WorkSummary['dcr'][number];
export type WorkSummaryExpenseRow = WorkSummary['expenses'][number];

const periodSearchShape = {
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  isActive: z.boolean().optional(),
};

export const systemDashboardSearchSchema = z.object({
  ...periodSearchShape,
  amUserId: z.coerce.number().optional(),
});

export const derDashboardSearchSchema = systemDashboardSearchSchema;

export const dcrDashboardSearchSchema = z.object({
  ...periodSearchShape,
  reportingMemberId: z.coerce.number().optional(),
  companyId: z.coerce.number().optional(),
  departmentId: z.coerce.number().optional(),
  divisionId: z.coerce.number().optional(),
  districtId: z.coerce.number().optional(),
  thanaId: z.coerce.number().optional(),
});

export const dcrWorkSummarySearchSchema = dcrDashboardSearchSchema.extend({
  dcrTypeId: z.coerce.number().optional(),
});

export type SystemDashboardSearch = z.infer<typeof systemDashboardSearchSchema>;
export type DcrDashboardSearch = z.infer<typeof dcrDashboardSearchSchema>;
export type DcrWorkSummarySearch = z.infer<typeof dcrWorkSummarySearchSchema>;
export type DerDashboardSearch = z.infer<typeof derDashboardSearchSchema>;
